import { db } from './localDB';
import { OfflineQueueItem, UUID, Chore, ChoreAssignment, Household, SyncActionType, SyncActionTypes, Schedule, calculateNextDueAfterCompletion, findStaleIds, pendingIds } from '../domain';
import { doc, collection, runTransaction, query, where, getDocsFromServer, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db as firestoreDB } from './firebaseConfig';

/** Bumped when every device must do one full sync, whatever its cursor says. Generation 1 cleans
 * out records only a full fetch can show are gone: chores deleted outright before soft delete
 * existed, and households left or deleted on another device (see findStaleIds). */
const FULL_SYNC_GENERATION = '1';

export class SyncService {
  async queueAction(
    type: SyncActionType,
    householdId: UUID | undefined,
    choreId: UUID | undefined,
    data: Record<string, unknown>,
  ): Promise<number> {
    const queueItem: OfflineQueueItem = {
      timestamp: new Date().toISOString(),
      type,
      choreId,
      householdId,
      data,
    };

    return db.offlineQueue.add(queueItem);
  }

  /**
   * Makes a local change and queues the action that syncs it in one Dexie transaction, so the two
   * are kept or lost together. A change kept without its action would never reach the server, and
   * the next full pull would take it for one undone elsewhere. `change` must only touch Dexie
   * (households, chores, activities): awaiting anything else ends the transaction early.
   * Gives back what `change` did and the queued action's id (which undoing a completion needs).
   */
  async queueWith<T>(
    change: () => Promise<T>,
    action: (result: T) => Parameters<SyncService['queueAction']>,
  ): Promise<{ result: T; queueItemId: number }> {
    return db.transaction('rw', [db.households, db.chores, db.activities, db.offlineQueue], async () => {
      const result = await change();
      const queueItemId = await this.queueAction(...action(result));
      return { result, queueItemId };
    });
  }

  async pushAction(action: OfflineQueueItem) {
    await this.syncAction(action);
    await this.forgetAction(action);
  }

  async forgetAction(action: OfflineQueueItem) {
    await db.offlineQueue.delete(action.id!);
  }

  // Atomically removes a queued action only if it's still there, so a caller can use the
  // boolean result to tell "still local-only, safely undone" apart from "already pushed to the
  // cloud" without a separate check-then-delete race against a concurrent sync cycle.
  async forgetActionIfPending(id: number): Promise<boolean> {
    return db.transaction('rw', db.offlineQueue, async () => {
      const existing = await db.offlineQueue.get(id);
      if (!existing) return false;
      await db.offlineQueue.delete(id);
      return true;
    });
  }

  getPendingActions(): Promise<OfflineQueueItem[]> {
    return db.offlineQueue.orderBy(":id").toArray();
  }

  private async syncAction(action: OfflineQueueItem) {
    // For household actions, use the householdId from the action
    // For chore actions, use the default household ID
    const householdId = action.householdId || "";

    switch (action.type) {
      case SyncActionTypes.CHORE_COMPLETED:
        await this.syncChoreCompleted(householdId, action);
        break;
      case SyncActionTypes.CHORE_CREATED:
        await this.syncChoreCreated(householdId, action);
        break;
      case SyncActionTypes.CHORE_DELETED:
        await this.syncChoreDeleted(householdId, action);
        break;
      case SyncActionTypes.CHORE_UPDATED:
        await this.syncChoreUpdated(householdId, action);
        break;
      case SyncActionTypes.HOUSEHOLD_CREATED:
        await this.syncHouseholdCreated(action);
        break;
      case SyncActionTypes.HOUSEHOLD_DELETED:
        await this.syncHouseholdDeleted(action);
        break;
      default:
        throw new Error(`Unknown action type: ${action.type}`);
    }
  }

  private async syncChoreCompleted(householdId: string, action: OfflineQueueItem) {
    if (!action.choreId) {
      throw new Error('ChoreCompleted action missing choreId');
    }

    const choreRef = doc(firestoreDB, `households/${householdId}/chores`, action.choreId);

    await runTransaction(firestoreDB, async (transaction) => {
      const choreDoc = await transaction.get(choreRef);

      if (!choreDoc.exists()) {
        throw new Error('Chore does not exist (may have been deleted by another user)');
      }

      const choreData = choreDoc.data();
      const lastCompletedAt = choreData?.last_completed_at;
      const completedAt = action.data.completedAt as string;

      // Only advance the denormalized last-completed cache (and next-due date) when this
      // completion is more recent than what's already recorded — an intentionally
      // backdated completion shouldn't move it backwards, but its activity is still logged below.
      if (!lastCompletedAt || new Date(completedAt) > new Date(lastCompletedAt)) {
        const schedule: Schedule = choreData?.schedule
          ?? { type: 'minutes', interval: choreData?.interval_frequency ?? 1440 };

        // updated_at drives the incremental sync cursor, so it must be a monotonic write-time
        // value from the server — never the (possibly backdated) completion time a user chose.
        const choreUpdate: Record<string, unknown> = {
          last_completed_at: completedAt,
          last_completed_by: action.data.byUser as string,
          updated_at: serverTimestamp(),
        };
        if (schedule.type !== 'once') {
          choreUpdate.next_due_at = calculateNextDueAfterCompletion(schedule, new Date(completedAt)).toISOString();
        }
        transaction.update(choreRef, choreUpdate);
      }

      // Add activity using transaction
      const activitiesRef = collection(firestoreDB, `households/${householdId}/activities`);
      const activityRef = doc(activitiesRef);  // Generate a new document reference
      transaction.set(activityRef, {
        chore_id: action.choreId,
        completed_at: action.data.completedAt,
        by_user: action.data.byUser,
      });
    });
  }

  private async syncChoreDeleted(householdId: string, action: OfflineQueueItem) {
    if (!action.choreId) {
      throw new Error('ChoreDeleted action missing choreId');
    }

    const choreRef = doc(firestoreDB, `households/${householdId}/chores`, action.choreId);

    await runTransaction(firestoreDB, async (transaction) => {
      const choreDoc = await transaction.get(choreRef);

      if (!choreDoc.exists()) {
        return;
      }

      // Soft-delete so other clients can detect the removal via incremental sync.
      // updated_at is the server write-time, not the local action timestamp (see syncChoreCompleted).
      transaction.update(choreRef, {
        deleted_at: action.timestamp,
        updated_at: serverTimestamp(),
      });
    });
  }

  private async syncChoreCreated(householdId: string, action: OfflineQueueItem) {
    if (!action.choreId) {
      throw new Error('ChoreCreated action missing choreId');
    }

    const choreRef = doc(firestoreDB, `households/${householdId}/chores`, action.choreId);

    await runTransaction(firestoreDB, async (transaction) => {
      const choreDoc = await transaction.get(choreRef);

      if (choreDoc.exists()) {
        // Chore already exists, this might be a conflict
        // For now, we'll assume the local creation takes precedence
        // In a more sophisticated system, we'd compare timestamps
        console.warn('Chore already exists during sync, skipping creation');
        return;
      }

      // Create the chore. updated_at is the server write-time (see syncChoreCompleted),
      // not the locally-recorded createdAt.
      transaction.set(choreRef, {
        name: action.data.name,
        description: action.data.description,
        labels: action.data.labels ?? [],
        schedule: action.data.schedule,
        household_id: householdId,
        created_at: action.data.createdAt,
        updated_at: serverTimestamp(),
        assigned_to: action.data.assigned_to ?? [],
      });
    });
  }

  private async syncChoreUpdated(householdId: string, action: OfflineQueueItem) {
    if (!action.choreId) {
      throw new Error('ChoreUpdated action missing choreId');
    }

    const choreRef = doc(firestoreDB, `households/${householdId}/chores`, action.choreId);

    await runTransaction(firestoreDB, async (transaction) => {
      const choreDoc = await transaction.get(choreRef);

      if (!choreDoc.exists()) {
        throw new Error('Chore does not exist (may have been deleted by another user)');
      }

      const choreData = choreDoc.data();

      // Simple conflict resolution: compare update timestamps
      // For now, we'll assume the action timestamp represents when the update was made locally
      // In a real app, you'd want to track update timestamps on the server side
      const actionTime = new Date(action.timestamp);
      const serverUpdateTime = choreData?.updated_at instanceof Timestamp
        ? choreData.updated_at.toDate()
        : new Date(0);

      if (serverUpdateTime > actionTime) {
        console.warn('Conflict detected: remote chore was updated more recently, skipping local update');
        return;
      }

      // Update the chore. updated_at is the server write-time (see syncChoreCompleted),
      // not the local action timestamp used above for conflict detection.
      transaction.update(choreRef, {
        name: action.data.name,
        description: action.data.description,
        labels: action.data.labels ?? [],
        schedule: action.data.schedule,
        assigned_to: action.data.assigned_to ?? [],
        updated_at: serverTimestamp(),
      });
    });
  }

  private async syncHouseholdCreated(action: OfflineQueueItem) {
    if (!action.householdId) {
      throw new Error('HouseholdCreated action missing householdId');
    }

    const householdRef = doc(firestoreDB, 'households', action.householdId);

    await runTransaction(firestoreDB, async (transaction) => {
      const householdDoc = await transaction.get(householdRef);

      if (householdDoc.exists()) {
        // Household already exists, might be a conflict
        console.warn('Household already exists during sync, skipping creation');
        return;
      }

      // Create the household. The names go with the ids: the next pull replaces the local household
      // with what's written here, and accepting an invitation appends to member_display_names, so
      // without them the owner's name is lost and the new member's lands in the owner's place.
      transaction.set(householdRef, {
        name: action.data.name,
        created_at: action.data.created_at,
        owner_id: action.data.owner_id,
        owner_display_name: action.data.owner_display_name,
        member_ids: action.data.member_ids,
        member_display_names: action.data.member_display_names,
      });
    });
  }

  private async syncHouseholdDeleted(action: OfflineQueueItem) {
    if (!action.householdId) {
      throw new Error('HouseholdDeleted action missing householdId');
    }

    const householdRef = doc(firestoreDB, 'households', action.householdId);

    await runTransaction(firestoreDB, async (transaction) => {
      const householdDoc = await transaction.get(householdRef);

      if (!householdDoc.exists()) {
        // Household already deleted or doesn't exist, nothing to do
        return;
      }

      // Delete the household
      transaction.delete(householdRef);
    });
  }

  private async getLastSyncedAt(): Promise<string | null> {
    const setting = await db.settings.get('lastSyncedAt');
    return setting?.value ?? null;
  }

  private async saveLastSyncedAt(timestamp: string): Promise<void> {
    await db.settings.put({ key: 'lastSyncedAt', value: timestamp });
  }

  async syncRemoteFromFirestore(userId: string, options: { forceFull?: boolean } = {}) {
    const generationIsCurrent = (await db.settings.get('fullSyncGeneration'))?.value === FULL_SYNC_GENERATION;
    // The cursor is the device's, not the account's: it says how far the last account synced here
    // got. Another account (signed into after this one signed out, or from local mode) has its
    // unchanged chores before that point, so for it the cursor is no good and it starts with a full
    // fetch. A device synced before the cursor was tied to an account does one full fetch, too.
    const cursorIsTheirs = (await db.settings.get('lastSyncedUid'))?.value === userId;
    const lastSyncedAt = options.forceFull || !generationIsCurrent || !cursorIsTheirs ? null : await this.getLastSyncedAt();
    // Anything created locally from here on can't be in what the server sends back, so the
    // clean-up below must leave it alone.
    const fetchStartedAt = new Date();

    const householdsRef = collection(firestoreDB, "households");

    // Find all households where the 'member_ids' array contains the current user's ID
    const q = query(householdsRef, where("member_ids", "array-contains", userId));

    // From the server, never a cache: what's missing from this answer gets removed locally.
    const querySnapshot = await getDocsFromServer(q);

    const households = querySnapshot.docs.map((snapshot) => {
      const data = snapshot.data();
      return {
        id: snapshot.id,
        name: data.name as string,
        created_at: data.created_at as string,
        owner_id: data.owner_id as string,
        owner_display_name: data.owner_display_name as string,
        member_ids: data.member_ids as string[],
        member_display_names: data.member_display_names as string[] || [],
        child_ids: data.child_ids as string[] ?? [],
      } as Household;
    });

    await db.transaction('rw', db.households, async () => {
      await db.households.bulkPut(households);
    });

    const householdIds: string[] = households.map(h => h.id);

    // The query returns every household the user is a member of, so one missing from it has been
    // left or deleted elsewhere: remove it here too, with its chores.
    await this.removeStaleHouseholds(householdIds, fetchStartedAt);

    // Advance the cursor using the server-observed updated_at values we actually saw, not the
    // reading device's own clock — a client clock reading is never a safe stand-in for "the
    // latest write time on the server" (see mergeRemoteFirestoreData for why updated_at itself
    // must be a serverTimestamp()).
    const observedMaxes = await Promise.all(
      householdIds.map(id => this.mergeRemoteFirestoreData(id, lastSyncedAt, fetchStartedAt))
    );
    const newCursor = observedMaxes.reduce<string | null>(
      (max, ts) => (ts && (!max || ts > max) ? ts : max),
      lastSyncedAt
    );

    if (newCursor) await this.saveLastSyncedAt(newCursor);
    await db.settings.put({ key: 'lastSyncedUid', value: userId });
    if (!generationIsCurrent) await db.settings.put({ key: 'fullSyncGeneration', value: FULL_SYNC_GENERATION });
  }

  private async removeStaleHouseholds(remoteIds: string[], fetchStartedAt: Date) {
    // The queue is read in the same transaction as the delete, so an action queued meanwhile counts.
    await db.transaction('rw', [db.households, db.chores, db.activities, db.offlineQueue], async () => {
      const pending = pendingIds(await db.offlineQueue.toArray());
      const stale = findStaleIds(await db.households.toArray(), remoteIds, pending.householdIds, fetchStartedAt);
      if (stale.length === 0) return;
      const choreIds = await db.chores.where('household_id').anyOf(stale).primaryKeys();
      await db.activities.where('chore_id').anyOf(choreIds).delete();
      await db.chores.bulkDelete(choreIds);
      await db.households.bulkDelete(stale);
    });
  }

  // Fetches chores updated since lastSyncedAt (full fetch when null), returning the latest
  // updated_at observed (as an ISO string) so the caller can advance the sync cursor, or null
  // if nothing came back.
  // Soft-deleted chores (deleted_at set) are removed from local DB along with their activities.
  // On a full fetch, so are local chores the server no longer has at all (see findStaleIds).
  // last_completed_at/by on the chore document replaces the activities collection read.
  private async mergeRemoteFirestoreData(householdId: string, lastSyncedAt: string | null, fetchStartedAt: Date): Promise<string | null> {
    const choresRef = collection(firestoreDB, `households/${householdId}/chores`);
    const choresQuery = lastSyncedAt
      ? query(choresRef, where('updated_at', '>', Timestamp.fromDate(new Date(lastSyncedAt))))
      : choresRef;
    const choresSnapshot = await getDocsFromServer(choresQuery);

    const activeChores: Chore[] = [];
    const deletedChoreIds: string[] = [];
    let maxUpdatedAt: string | null = null;

    choresSnapshot.docs.forEach((snapshot) => {
      const data = snapshot.data();

      if (data.updated_at instanceof Timestamp) {
        const updatedAtIso = data.updated_at.toDate().toISOString();
        if (!maxUpdatedAt || updatedAtIso > maxUpdatedAt) maxUpdatedAt = updatedAtIso;
      }

      if (data.deleted_at) {
        deletedChoreIds.push(snapshot.id);
        return;
      }
      activeChores.push({
        id: snapshot.id,
        name: data.name as string,
        description: data.description as string | undefined,
        labels: data.labels as string[] ?? [],
        schedule: (data.schedule ?? { type: 'minutes', interval: data.interval_frequency ?? 1440 }) as Schedule,
        household_id: data.household_id as string,
        created_at: data.created_at as string,
        assigned_to: data.assigned_to as ChoreAssignment[] ?? [],
        last_completed_at: data.last_completed_at as string | undefined,
        last_completed_by: data.last_completed_by as string | undefined,
      } as Chore);
    });

    await db.transaction('rw', [db.chores, db.activities, db.offlineQueue], async () => {
      if (activeChores.length > 0) await db.chores.bulkPut(activeChores);

      // Only a full fetch lists every chore, so only then can a missing one be told from an
      // unchanged one.
      if (!lastSyncedAt) {
        const pending = pendingIds(await db.offlineQueue.toArray());
        const local = await db.chores.where('household_id').equals(householdId).toArray();
        const remoteIds = choresSnapshot.docs.map(snapshot => snapshot.id);
        deletedChoreIds.push(...findStaleIds(local, remoteIds, pending.choreIds, fetchStartedAt));
      }

      if (deletedChoreIds.length > 0) {
        await db.chores.bulkDelete(deletedChoreIds);
        await db.activities.where('chore_id').anyOf(deletedChoreIds).delete();
      }
    });

    return maxUpdatedAt;
  }
}

export const syncService = new SyncService();